import type { RefinementCtx } from 'zod';
import { z } from 'zod';

import { type Countries } from '@ctct/domain-layer';

import type { TranslationGetter, TranslationPaths } from '../../../../types';

export class ValidationService {
  private VALID_CHARS = /^[^`'\\!~%^:;|/?<>()]*$/;

  // allow number, space, + and - signs
  private PHONE_CHARACTERS_ONLY = /^[0-9 +-]*$/;

  constructor(private countries: Countries, private t: TranslationGetter) {}

  public api() {
    return {};
  }

  public data() {
    return {};
  }

  public handlers() {
    return {};
  }

  // validation helpers
  public getFirstNameValidationSchema() {
    const MIN_LENGTH = 2;
    const MAX_LENGTH = 16;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH);
  }

  public getLastNameValidationSchema() {
    const MIN_LENGTH = 2;
    const MAX_LENGTH = 31;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH);
  }

  public getCompanyNameValidationSchema() {
    const MIN_LENGTH = 2;
    const MAX_LENGTH = 101;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH);
  }

  public getAddress1ValidationSchema() {
    const MIN_LENGTH = 2;
    const MAX_LENGTH = 101;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH);
  }

  public getAddress2ValidationSchema() {
    const MIN_LENGTH = 0;
    const MAX_LENGTH = 101;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH);
  }

  public getCurrentTaxAgreementSchema() {
    return z.boolean().nullish();
  }

  public getCityValidationSchema() {
    const MIN_LENGTH = 1;
    const MAX_LENGTH = 51;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH);
  }

  public getZipCodeValidationSchema() {
    return z
      .string()
      .trim()
      .regex(this.VALID_CHARS, {
        message: this.t('application-layer.form-errors.incorrect-chars'),
      });
  }

  public validateZipCode(zipcode: string, countryCode: string, ctx: RefinementCtx) {
    // UK postal code regex (allows alphanumeric combinations with optional space)
    const ukZipRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;

    // Australia postal code regex (4 digits)
    const auZipRegex = /^\d{4}$/;

    // New Zealand postal code regex (4 digits)
    const nzZipRegex = /^\d{4}$/;

    // India postal code (PIN code) regex (6 digits)
    const inZipRegex = /^\d{6}$/;

    // US ZIP code regex (5 digits or 9 digits with optional dash)
    const usZipRegex = /^\d{5}(-\d{4})?$/;

    // Canadian postal code regex: Letter-Digit-Letter Space Digit-Letter-Digit (optional space)
    const caZipRegex = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i;

    // Min and max value for other countries
    const MIN = 3;
    const MAX = 10;

    switch (countryCode) {
      case 'GB':
        if (!ukZipRegex.test(zipcode)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: this.t('application-layer.form-errors.zipcode-validation-uk'),
            path: ['zipcode'],
          });
        }

        break;

      case 'AU':
        if (!auZipRegex.test(zipcode)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: this.t('application-layer.form-errors.zipcode-validation-au'),
            path: ['zipcode'],
          });
        }

        break;

      case 'NZ':
        if (!nzZipRegex.test(zipcode)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: this.t('application-layer.form-errors.zipcode-validation-nz'),
            path: ['zipcode'],
          });
        }

        break;

      case 'IN':
        if (!inZipRegex.test(zipcode)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: this.t('application-layer.form-errors.zipcode-validation-in'),
            path: ['zipcode'],
          });
        }

        break;

      case 'US':
        if (!usZipRegex.test(zipcode)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: this.t('application-layer.form-errors.zipcode-validation-us'),
            path: ['zipcode'],
          });
        }

        break;

      case 'CA':
        if (!caZipRegex.test(zipcode)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: this.t('application-layer.form-errors.zipcode-validation-ca'),
            path: ['zipcode'],
          });
        }

        break;

      default:
        if (zipcode.length < MIN) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: this.t('application-layer.form-errors.min-allowed', { count: MIN }),
            path: ['zipcode'],
          });
        }

        if (zipcode.length > MAX) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: this.t('application-layer.form-errors.max-allowed', { count: MAX }),
            path: ['zipcode'],
          });
        }

        break;
    }
  }

  public validateState(countryCode: string, state: string | undefined, ctx: RefinementCtx) {
    const stateRequired = this.countries.isShowingStates(countryCode);

    if (stateRequired && !state) {
      const translationPath: TranslationPaths = this.countries.getCountryHasProvinces(countryCode)
        ? 'application-layer.form-errors.province-required'
        : 'application-layer.form-errors.state-required';

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: this.t(translationPath),
        path: ['state'],
      });
    }
  }

  public validateVatNumbers(
    country: string,
    currentTaxAgreement: boolean | null,
    vatNumber: string,
    ctx: RefinementCtx
  ) {
    if (!vatNumber) {
      if (currentTaxAgreement) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: this.t('application-layer.form-errors.if-agreement-checked-enter-vat'),
          path: ['currentTaxAgreement'],
        });
      }

      return;
    }

    if (this.countries.isShowingTaxAgreement(country) && !currentTaxAgreement) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: this.t('application-layer.form-errors.vat-agreement-must-be-checked'),
        path: ['currentTaxAgreement'],
      });
    }

    // EU & UK
    if (this.countries.getTaxType(country) === 'vat' && vatNumber.length > 12) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: this.t('application-layer.form-errors.eu-vat-length'),
        path: ['vatNumber'],
      });
    }

    // Australia
    if (this.countries.getTaxType(country) === 'abn') {
      if (vatNumber.length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: this.t('application-layer.form-errors.abn-length'),
          path: ['vatNumber'],
        });
      }

      const abnChecksum = (num: string) => {
        const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
        const abn = num.replace(/[^\d]/, '');
        let result = false;

        // check length is 11 digits
        if (abn.length === 11) {
          // apply ato check method
          let sum = 0;
          let weight;

          // eslint-disable-next-line no-plusplus
          for (let index = 0; index <= weights.length - 1; index++) {
            weight = weights[index];

            const digit = parseInt(abn[index], 10) - (index ? 0 : 1);

            sum += weight * digit;
          }
          result = sum % 89 === 0;
        }

        return result;
      };

      if (!abnChecksum(vatNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: this.t('application-layer.form-errors.abn-checksum'),
          path: ['vatNumber'],
        });
      }
    }

    // India
    if (this.countries.getTaxType(country) === 'gstin') {
      if (vatNumber.length !== 15) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: this.t('application-layer.form-errors.gstin-length'),
          path: ['vatNumber'],
        });
      }

      const gstinRegex = /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/;

      if (!gstinRegex.test(vatNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: this.t('application-layer.form-errors.gstin-regex'),
          path: ['vatNumber'],
        });
      }
    }

    // Canada
    if (country === 'CA') {
      // validation of Canada GST/HST numbers seems to be more complex and it depedns on the province
      // for now we are going to use regex which was used in the bp console single bill form
      const caRegex = /^[a-zA-Z0-9_.-]*$/;

      if (!caRegex.test(vatNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: this.t('application-layer.form-errors.gstin-regex'),
          path: ['vatNumber'],
        });
      }
    }
  }

  public getStateValidationSchema() {
    return z.string().optional();
  }

  public getCountryValidationSchema() {
    const MIN_LENGTH = 1;
    const MAX_LENGTH = 150;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH);
  }

  public getPhoneValidationSchema() {
    const MIN_LENGTH = 5;
    const MAX_LENGTH = 21;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH).regex(this.PHONE_CHARACTERS_ONLY, {
      message: this.t('application-layer.form-errors.numbers-only'),
    });
  }

  public getEmailValidationSchema() {
    const MIN_LENGTH = 0;
    const MAX_LENGTH = 81;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH).email({
      message: this.t('application-layer.form-errors.incorrect-email'),
    });
  }

  public getVatValidationSchema() {
    const MIN_LENGTH = 0;
    const MAX_LENGTH = 81;

    return this.createStringValidationSchema(MIN_LENGTH, MAX_LENGTH).transform((vat) => vat.replace(/\s+/g, ''));
  }

  public createStringValidationSchema(min: number, max: number) {
    return z
      .string()
      .trim()
      .regex(this.VALID_CHARS, {
        message: this.t('application-layer.form-errors.incorrect-chars'),
      })
      .min(min, {
        message: this.t('application-layer.form-errors.min-allowed', { count: min }),
      })
      .max(max, {
        message: this.t('application-layer.form-errors.max-allowed', { count: max }),
      });
  }
}
